import React, { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';
import CallbackPage from '../routes/CallbackPage';
import SignoutCallbackComponent from '../routes/SignoutCallbackComponent';
import LegacyClient from './legacyOIDCClient';
import NextClient from './nextOIDCClient';
import DatastoreLanding from '../routes/DatastoreLanding'; // import your page

function _isAbsoluteUrl(url) {
  return url.includes('http://') || url.includes('https://');
}

function _makeAbsoluteIfNecessary(url, base_url) {
  if (_isAbsoluteUrl(url)) return url;
  if (base_url.endsWith('/')) base_url = base_url.slice(0, -1);
  return base_url + url;
}

const initUserManager = (oidc, routerBasename) => {
  if (!oidc || !oidc.length) return;

  const firstOpenIdClient = oidc[0];
  const { protocol, host } = window.location;
  const baseUri = `${protocol}//${host}${routerBasename}`;

  const redirect_uri = firstOpenIdClient.redirect_uri || '/callback';
  const silent_redirect_uri = firstOpenIdClient.silent_redirect_uri || '/silent-refresh.html';
  const post_logout_redirect_uri = firstOpenIdClient.post_logout_redirect_uri || '/';

  const openIdConnectConfiguration = Object.assign({}, firstOpenIdClient, {
    redirect_uri: _makeAbsoluteIfNecessary(redirect_uri, baseUri),
    silent_redirect_uri: _makeAbsoluteIfNecessary(silent_redirect_uri, baseUri),
    post_logout_redirect_uri: _makeAbsoluteIfNecessary(post_logout_redirect_uri, baseUri),
  });

  const client = firstOpenIdClient.response_type === 'code' ? NextClient : LegacyClient;
  return client(openIdConnectConfiguration);
};

function LogoutComponent({ userManager }) {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  localStorage.setItem('signoutEvent', 'true');
  userManager.signoutRedirect({
    post_logout_redirect_uri: query.get('redirect_uri'),
  });
  return null;
}

function LoginComponent({ userManager, oidcAuthority }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const iss = queryParams.get('iss');
  const loginHint = queryParams.get('login_hint');
  const targetLinkUri = queryParams.get('target_link_uri');

  if (iss !== oidcAuthority) {
    console.error('iss of /login does not match the oidc authority');
    return null;
  }

  useEffect(() => {
    userManager.removeUser().then(() => {
      // always redirect to /datastore-landing
      const ohifRedirectTo = { pathname: '/datastore-landing' };
      sessionStorage.setItem('ohif-redirect-to', JSON.stringify(ohifRedirectTo));

      if (loginHint) {
        userManager.signinRedirect({ login_hint: loginHint });
      } else {
        userManager.signinRedirect();
      }
    });
  }, [userManager, loginHint]);

  return null;
}

function OpenIdConnectRoutes({ oidc, routerBasename, userAuthenticationService }) {
  const navigate = useNavigate();
  const location = useLocation();
  const userManager = initUserManager(oidc, routerBasename);

  const getAuthorizationHeader = () => {
    const user = userAuthenticationService.getUser();
    if (!user) return;
    return { Authorization: `Bearer ${user.access_token}` };
  };

  const handleUnauthenticated = () => {
    userManager.signinRedirect();
    return null;
  };

  useEffect(() => {
    userAuthenticationService.set({ enabled: true });
    userAuthenticationService.setServiceImplementation({
      getAuthorizationHeader,
      handleUnauthenticated,
    });
  }, []);

  useEffect(() => {
    const userLoadedHandler = user => userAuthenticationService.setUser(user);
    userManager.events.addUserLoaded(userLoadedHandler);
    return () => userManager.events.removeUserLoaded(userLoadedHandler);
  }, []);

  // Multi-tab logout
  useEffect(() => {
    localStorage.removeItem('signoutEvent');
    const storageEventListener = event => {
      if (localStorage.getItem('signoutEvent')) {
        navigate(`/logout?redirect_uri=${encodeURIComponent(window.location.href)}`);
      }
    };
    window.addEventListener('storage', storageEventListener);
    return () => window.removeEventListener('storage', storageEventListener);
  }, []);

  const redirectURI = new URL(userManager.settings.redirect_uri).pathname;
  const silent_refresh_uri = new URL(userManager.settings.silent_redirect_uri).pathname;
  const post_logout_redirect_uri = new URL(userManager.settings.post_logout_redirect_uri).pathname;

  return (
    <Routes>
      <Route
        path={silent_refresh_uri}
        onEnter={window.location.reload}
      />
      <Route
        path={post_logout_redirect_uri}
        element={
          <SignoutCallbackComponent
            userManager={userManager}
            successCallback={() => console.log('Signout successful')}
            errorCallback={err => console.warn('Signout failed', err)}
          />
        }
      />
      <Route
        path={redirectURI}
        element={
          <CallbackPage
            userManager={userManager}
            onRedirectSuccess={user => {
              userAuthenticationService.setUser(user);
              // Force redirect to DatastoreLanding after login
              navigate('/datastore-landing');
            }}
          />
        }
      />
      <Route
        path="/login"
        element={
          <LoginComponent
            userManager={userManager}
            oidcAuthority={oidc[0].authority}
          />
        }
      />
      <Route
        path="/logout"
        element={<LogoutComponent userManager={userManager} />}
      />
      {/* Optional route if you want to allow direct access */}
      <Route
        path="/datastore-landing"
        element={<DatastoreLanding />}
      />
    </Routes>
  );
}

export default OpenIdConnectRoutes;
